import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang, LANGS } from "@/context/LanguageContext";
import { OWNER } from "@/data/villas";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function Logo({ scrolled }) {
  return (
    <Link to="/" data-testid="nav-logo" className="flex flex-col leading-none">
      <span className={`font-serif text-2xl tracking-[0.06em] transition-colors ${scrolled ? "text-[color:hsl(var(--dh-ink))]" : "text-white"}`}>
        DAR HASNA
      </span>
      <span className={`text-[9px] tracking-[0.42em] mt-1 transition-colors ${scrolled ? "text-[color:hsl(var(--dh-muted))]" : "text-white/70"}`}>
        COLLECTION
      </span>
    </Link>
  );
}

function LanguageSwitcher({ scrolled }) {
  const { lang, setLang, t } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="lang-switch"
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] transition-colors hover:text-[color:hsl(var(--dh-terracotta))] ${scrolled ? "text-[color:hsl(var(--dh-ink))]" : "text-white"}`}
      >
        <Globe className="h-3.5 w-3.5" />
        {t.lang[lang]}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[6rem]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l}
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className="uppercase tracking-widest text-xs"
          >
            {t.lang[l]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/villa/hasna", label: t.nav.villaHasna, id: "hasna" },
    { to: "/services", label: t.nav.services, id: "services" },
    { to: "/sejour-famille", label: t.nav.family, id: "family" },
    { to: "/seniors", label: t.nav.seniors, id: "seniors" },
    { to: "/events", label: t.nav.events, id: "events" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled || open
          ? "bg-[color:hsl(var(--dh-sand))]/95 backdrop-blur-md border-b border-[color:hsl(var(--dh-ocre)/0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="dh-container flex items-center justify-between h-20">
        <Logo scrolled={scrolled || open} />
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase transition-colors ${
                  isActive
                    ? "text-[color:hsl(var(--dh-terracotta))]"
                    : scrolled
                      ? "text-[color:hsl(var(--dh-ink))] hover:text-[color:hsl(var(--dh-terracotta))]"
                      : "text-white hover:text-[color:hsl(var(--dh-ocre))] [text-shadow:_0_1px_2px_rgb(0_0_0_/_35%)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-5">
          <LanguageSwitcher scrolled={scrolled || open} />
          <Link to="/contact" data-testid="nav-book-btn" className="hidden md:inline-flex dh-btn-primary py-2 px-4 text-xs">
            {t.nav.book}
          </Link>
          <button
            data-testid="mobile-menu-btn"
            className={`lg:hidden transition-colors ${scrolled || open ? "text-[color:hsl(var(--dh-ink))]" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-[color:hsl(var(--dh-sand))] border-t border-[color:hsl(var(--dh-ocre)/0.15)]">
          <nav className="dh-container flex flex-col py-6 gap-4">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`mnav-${l.id}`}
                className="text-sm uppercase tracking-[0.2em] py-1"
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-[color:hsl(var(--dh-palm))] text-[color:hsl(var(--dh-sand))] mt-24">
      <div className="dh-container py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="font-serif text-2xl tracking-[0.06em]">DAR HASNA</div>
          <div className="text-[9px] tracking-[0.42em] opacity-70 mt-1">COLLECTION</div>
          <p className="mt-6 text-sm opacity-80 leading-relaxed">{t.footer.tagline}</p>
        </div>
        <div>
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))] mb-4">{t.footer.quickLinks}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/villa/hasna" className="dh-link-underline">{t.nav.villaHasna}</Link></li>
            <li><Link to="/services" className="dh-link-underline">{t.nav.services}</Link></li>
            <li><Link to="/events" className="dh-link-underline">{t.nav.events}</Link></li>
          </ul>
        </div>
        <div>
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))] mb-4">{t.contact.title}</div>
          <ul className="space-y-2 text-sm opacity-90">
            <li>{t.contact.addressValue}</li>
            <li><a href={`mailto:${OWNER.email}`} className="dh-link-underline">{OWNER.email}</a></li>
            <li><a href={OWNER.waLink} target="_blank" rel="noreferrer" className="dh-link-underline">WhatsApp</a></li>
          </ul>
        </div>
        <div>
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))] mb-4">{t.contact.followUs}</div>
          <ul className="space-y-2 text-sm opacity-90">
            <li><a href={OWNER.instagram} target="_blank" rel="noreferrer" className="dh-link-underline">Instagram</a></li>
            <li><a href={OWNER.facebook} target="_blank" rel="noreferrer" className="dh-link-underline">Facebook</a></li>
            <li><a href={OWNER.tiktok} target="_blank" rel="noreferrer" className="dh-link-underline">TikTok</a></li>
            <li><a href={OWNER.snapchat} target="_blank" rel="noreferrer" className="dh-link-underline">Snapchat</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="dh-container py-6 text-xs opacity-70 flex flex-col md:flex-row justify-between gap-2">
          <div>{t.footer.copyright}</div>
          <div>Palmeraie · Marrakech · Maroc</div>
        </div>
      </div>
    </footer>
  );
}

export function WhatsAppFloat() {
  return (
    <a
      href={OWNER.waLink}
      target="_blank"
      rel="noreferrer"
      aria-label="WhatsApp"
      data-testid="whatsapp-float"
      className="fixed bottom-6 end-6 z-50 flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] text-white shadow-lg shadow-black/20 hover:scale-110 transition-transform duration-300"
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden>
        <path d="M20.52 3.48A11.9 11.9 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.85 11.85 0 0 0 5.65 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.23-6.15-3.39-8.43ZM12.06 21.5h-.01a9.65 9.65 0 0 1-4.92-1.35l-.35-.21-3.8 1 1.02-3.7-.23-.38a9.66 9.66 0 1 1 17.94-5.02c0 5.34-4.34 9.66-9.65 9.66Zm5.55-7.24c-.3-.15-1.79-.88-2.07-.98-.28-.1-.48-.15-.68.15-.2.3-.78.98-.96 1.18-.18.2-.36.22-.66.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.5-1.79-1.67-2.1-.18-.3-.02-.46.13-.61.14-.14.3-.36.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.68-1.63-.94-2.24-.25-.6-.5-.5-.68-.5h-.58c-.2 0-.53.07-.8.38-.28.3-1.06 1.03-1.06 2.51 0 1.48 1.08 2.91 1.23 3.11.15.2 2.13 3.26 5.16 4.57.72.31 1.28.5 1.72.63.72.23 1.38.2 1.9.12.58-.09 1.79-.73 2.05-1.44.25-.7.25-1.31.18-1.43-.08-.13-.28-.2-.58-.34Z" />
      </svg>
    </a>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App relative overflow-x-hidden">
      <Header />
      <main className="pt-20">{children}</main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
